import { useForm, SubmitHandler } from 'react-hook-form'
import { Label } from '../ui/label'
import { Input } from '../ui/input'
import ActionButton from '../common/ActionButton'
import Link from 'next/link'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { consumerRequest } from '@/helper/request'
import { LOGIN_API } from '@/helper/apiConstants'
import { dispatch, useSelector } from '@/lib/store'
import { resetLoginData, updateLoginData } from '@/lib/reducers/loginReducer'
import { useEffect, useState } from 'react'
import { IFormInput } from '@/utils/interfaces'
import useCustomToast from '@/hooks/useCustomToast'
import {
  CURRENT_SUBSIDIARY,
  mobileNumberRegexEx,
  PRIVACY_POLICY_PAGE,
  TERMS_AND_CONDITIONS_PAGE,
} from '@/utils/constants'
import { useRouter } from 'next/navigation'

const LoginStep1 = ({ isMobile }: any) => {
  const showToast = useCustomToast()
  const router = useRouter()
  const loginState = useSelector((state) => state.login) || {}

  const [loading, setLoading] = useState(false)
  const validationSchema = yup
    .object({
      mobileNumber: yup
        .string()
        .required('Please provide a valid 10-digit phone number')
        .matches(
          mobileNumberRegexEx,
          // 'Mobile number must be exactly 10 digits'
          'Please provide a valid 10-digit phone number'
        ),
    })
    .required()

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    clearErrors,
    setValue,
  } = useForm({
    defaultValues: {
      mobileNumber: loginState?.phoneNumber ? loginState?.phoneNumber : '',
    },
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  })

  const handleKeyPress = (event: any) => {
    // Ensure that user doesn't input any characters
    if (!/[0-9]/.test(event.key) && event.key !== 'Backspace') {
      event.preventDefault()
    }
    if (event.key === 'Enter') {
      handleSubmit(onSubmit)()
    }
  }

  const onSubmit: SubmitHandler<IFormInput> = async (data) => {
    setLoading(true)
    const formData = {
      phoneNumber: data?.mobileNumber,
      subsidiary: CURRENT_SUBSIDIARY,
    }
    const apiRes: any = await consumerRequest(LOGIN_API, 'post', formData)
    if (apiRes?.status === true) {
      dispatch(
        updateLoginData({
          ...loginState,
          phoneNumber: data?.mobileNumber,
          hasOTPSent: true,
          hasOTPResent: false,
          activeStep: 2,
        })
      )
    }
    if (apiRes?.status === false) {
      showToast('danger', apiRes?.message || 'Failed to send OTP')
    }
    setLoading(false)
  }
  const mobileNumber = watch('mobileNumber', '')

  useEffect(() => {
    setValue(
      'mobileNumber',
      loginState?.phoneNumber ? loginState?.phoneNumber : ''
    )
  }, [setValue, loginState?.phoneNumber])

  useEffect(() => {
    const preventZoom = (event: WheelEvent) => {
      if (event.ctrlKey || event.metaKey) {
        // Prevent zoom (Ctrl + Mouse Wheel or Cmd + Mouse Wheel)
        event.preventDefault()
      }
    }

    document.addEventListener('wheel', preventZoom, { passive: false })

    return () => {
      document.removeEventListener('wheel', preventZoom)
    }
  }, [])

  return (
    <div
      className={`w-full ${
        !isMobile ? 'pt-16 border-l border-solid border-gray-100' : ''
      }`}
    >
      <div className="border-b border-solid border-gray-100">
        <h2 className="text-body-lg sm:text-heading-md font-bold text-primary mb-2 text-center">
          Welcome to DDRC Agilus Diagnostics
        </h2>
        <p className="text-body-sm sm:text-body-md xl:text-label-lg font-semibold text-gray-700 mb-6 text-center">
          Care begins with us
        </p>
      </div>
      <div
        className={`py-6 border-b border-solid border-gray-100 ${
          !isMobile ? ' px-10' : ''
        }`}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Label htmlFor="number" className="text-label-xs text-gray-700 mb-1">
            Enter Mobile Number*
          </Label>
          <div className="mobile_number_input">
            <Input
              {...register('mobileNumber', { required: true })}
              autoComplete="off"
              type="number"
              className={`w-full bg-gray-100 pl-12 ${
                errors.mobileNumber ? 'ring-1 ring-danger-200' : ''
              }`}
              onKeyDown={(e) => handleKeyPress(e)}
              onScroll={(e) => e.preventDefault()}
              // onResize={(e) => e.preventDefault()}
              onChange={(e) => {
                // Ensure the value doesn't exceed 10 digits
                if (e.target.value.length >= 10) {
                  e.target.value = e.target.value.slice(0, 10)
                } else {
                  clearErrors('mobileNumber')
                }
                setValue('mobileNumber', e.target.value)
              }}
            />
          </div>
          {!!errors?.mobileNumber?.message ? (
            <p className="text-label-xs text-danger-500 mt-1 mb-8 h-4">
              {errors.mobileNumber?.message}
            </p>
          ) : (
            <p className="text-label-xs text-gray-700 mt-1 mb-8">
              We&apos;ll send an OTP to verify
            </p>
          )}
          <ActionButton
            variant="primary"
            type="submit"
            loading={loading}
            disabled={
              Boolean(errors.mobileNumber) ||
              mobileNumber.length !== 10 ||
              loading
            }
            title="Continue"
            className="w-full py-3 mb-4"
          />
        </form>
        {/* <div className="flex gap-2 items-center justify-center">
          <p className="text-label-sm text-gray-700 text-center">Already having an account?</p>
          <Link title="Sign in" href={"/"} className="text-primary text-label-sm">
            Sign in
          </Link>
        </div> */}
      </div>
      <p className="text-label-xs text-gray-700 my-3 text-center">
        By clicking, you agree to our{' '}
        <Link
          className="text-gray-800 underline"
          title="T&C"
          href={TERMS_AND_CONDITIONS_PAGE}
          onClick={() => {
            dispatch(resetLoginData())
            router.push(TERMS_AND_CONDITIONS_PAGE)
          }}
        >
          T&C
        </Link>{' '}
        and{' '}
        <Link
          className="text-gray-800 underline"
          title="Privacy Policy"
          href={PRIVACY_POLICY_PAGE}
          onClick={() => {
            dispatch(resetLoginData())
            router.push(PRIVACY_POLICY_PAGE)
          }}
        >
          Privacy Policy
        </Link>
      </p>
    </div>
  )
}

export default LoginStep1
